import type { Side } from './tradeRules';

/** The ways a user can type a stop loss or take profit. All of them resolve to a price before anything is sent. */
export const LEVEL_UNITS = ['price', 'points', 'pips', 'money', 'percent'] as const;
export type LevelUnit = (typeof LEVEL_UNITS)[number];

export const UNIT_LABELS: Record<LevelUnit, string> = {
  price: 'Price',
  points: 'Points',
  pips: 'Pips',
  money: 'Money',
  percent: '% of balance',
};

export interface LevelContext {
  side: Side;
  /** The price the distance is measured from: the fill price for a market order, the order price for a pending one. */
  entry: number;
  digits: number;
  /** Account currency per 1.0 of price movement for the order's volume (from the server's profit preview). */
  moneyPerPrice: number | null;
  balance: number;
}

/** One pip is 10 points on 3- and 5-digit symbols, and one point elsewhere. */
export const pipSize = (digits: number) => (digits === 3 || digits === 5 ? 10 : 1) / 10 ** digits;
export const pointSize = (digits: number) => 1 / 10 ** digits;

export type LevelKind = 'stopLoss' | 'takeProfit';

/**
 * The inverse of levelPrice: a price level written in another unit, for switching units without losing a level. Null when
 * the level lies on the other side of the entry (a stop loss already in profit, say), which only a price can express, or
 * when money is not known yet.
 */
export function levelText(unit: LevelUnit, price: number, kind: LevelKind, ctx: LevelContext): string | null {
  if (!(price > 0)) return '';
  if (unit === 'price') return price.toFixed(ctx.digits);
  const winning = kind === 'takeProfit';
  const up = (ctx.side === 'Buy') === winning;
  const distance = up ? price - ctx.entry : ctx.entry - price;
  if (!(distance > 0)) return null;
  if (unit === 'points') return String(Math.round(distance / pointSize(ctx.digits)));
  if (unit === 'pips') return String(Number((distance / pipSize(ctx.digits)).toFixed(1)));
  if (!ctx.moneyPerPrice || ctx.moneyPerPrice <= 0) return null;
  const money = distance * ctx.moneyPerPrice;
  if (unit === 'money') return money.toFixed(2);
  return ctx.balance > 0 ? String(Number(((money / ctx.balance) * 100).toFixed(2))) : null;
}

/**
 * The price a typed level means. Stop loss lies on the losing side of the entry, take profit on the winning side, so a
 * distance typed as points, pips, money or a share of the balance gives the same price whichever way it was entered.
 * Returns null when the text is not a usable number, or money cannot be converted yet.
 */
export function levelPrice(unit: LevelUnit, text: string, kind: LevelKind, ctx: LevelContext): number | null {
  if (text.trim() === '') return null;
  const value = Number(text);
  if (!Number.isFinite(value) || value <= 0) return null;
  if (unit === 'price') return Number(value.toFixed(ctx.digits));

  let distance: number;
  if (unit === 'points') distance = value * pointSize(ctx.digits);
  else if (unit === 'pips') distance = value * pipSize(ctx.digits);
  else {
    if (!ctx.moneyPerPrice || ctx.moneyPerPrice <= 0) return null;
    const money = unit === 'money' ? value : (value / 100) * ctx.balance;
    distance = money / ctx.moneyPerPrice;
  }
  const winning = kind === 'takeProfit';
  const up = (ctx.side === 'Buy') === winning;
  return Number((ctx.entry + (up ? distance : -distance)).toFixed(ctx.digits));
}
